'use client'

import { Carousel } from '@heroui-pro/react/carousel'
import { type ComponentProps, useEffect, useMemo, useRef, useState } from 'react'

import { Icon } from '@/components/ui/icon'
import { MediaType } from '@/lib/types/core'
import { IMediaItem } from '@/lib/types/media'

// Embla instance type, sourced from the Pro Carousel's own `setApi` prop so we
// never import the (eslint-banned) `embla-carousel` package directly.
type EmblaApi = Parameters<NonNullable<ComponentProps<typeof Carousel>['setApi']>>[0]

interface ProjectMediaCarouselProps {
  media: IMediaItem[]
  currentIndex: number
  onIndexChange: (index: number) => void
  onOpenLightbox: (index: number) => void
}

/**
 * Inline media carousel on the gallery detail page.
 *
 * - Horizontal swipe (touch) and arrow-key (focused) navigation via the Pro
 *   Carousel (Embla under the hood — the root handles arrow keys when focused).
 * - Tap / click the hero image opens the full Lightbox at the current slide.
 * - Overlay pagination dots + a thumbnail strip below both jump the carousel and
 *   stay snapped to the active slide.
 *
 * Single-slide projects skip the carousel entirely and render a plain image.
 */
export function ProjectMediaCarousel({
  media,
  currentIndex,
  onIndexChange,
  onOpenLightbox
}: ProjectMediaCarouselProps) {
  const [api, setApi] = useState<EmblaApi>()
  const thumbsRef = useRef<HTMLDivElement>(null)
  const startIndexRef = useRef(currentIndex)
  // Stable options object so embla-carousel-react never re-inits mid-life.
  const emblaOpts = useMemo(() => ({ startIndex: startIndexRef.current }), [])

  // Emit the active slide upward when Embla settles on a new one (swipe / dot).
  useEffect(() => {
    if (!api) return
    const onSelect = () => onIndexChange(api.selectedScrollSnap())
    api.on('select', onSelect)
    return () => {
      api.off('select', onSelect)
    }
  }, [api, onIndexChange])

  // Push an externally-driven index (thumbnail / lightbox sync) into Embla.
  useEffect(() => {
    if (!api) return
    if (api.selectedScrollSnap() !== currentIndex) api.scrollTo(currentIndex)
  }, [api, currentIndex])

  // Keep the active thumbnail visible in the scroll strip.
  useEffect(() => {
    const strip = thumbsRef.current
    if (!strip) return
    const btn = strip.querySelector<HTMLButtonElement>(`[data-index='${currentIndex}']`)
    if (btn) {
      btn.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
    }
  }, [currentIndex])

  if (media.length === 0) return null

  // Single image — no carousel needed, just a clickable hero.
  if (media.length === 1) {
    const item = media[0]
    return (
      <button
        type='button'
        onClick={() => onOpenLightbox(0)}
        className='block relative w-full overflow-hidden rounded-lg cursor-zoom-in aspect-square group'
        aria-label='Open image in full-screen viewer'
      >
        {item.type === MediaType.VIDEO ? (
          <video
            src={item.url}
            poster={item.metadata?.thumbnailUrl}
            className='object-cover w-full h-full'
            muted
            loop
            autoPlay
            playsInline
          />
        ) : (
          <img src={item.url} alt={item.altText || ''} className='object-cover w-full h-full' />
        )}
      </button>
    )
  }

  return (
    <div className='min-w-0 space-y-3'>
      {/* min-w-0 + overflow-hidden here clip the Embla track BEFORE the library's
          lazy CSS lands; without them the 5-slide flex row (5 x 412px) briefly
          lays out unclipped after hydration and Chrome ratchets the mobile
          layout viewport wider for good. */}
      <div className='relative min-w-0 rounded-lg overflow-hidden bg-black/5'>
        <Carousel opts={emblaOpts} setApi={setApi} className='overflow-hidden [--carousel-gap:0px]'>
          <Carousel.Content>
            {media.map((item, i) => (
              <Carousel.Item key={item.id || i}>
                <button
                  type='button'
                  onClick={() => onOpenLightbox(i)}
                  className='block relative w-full overflow-hidden cursor-zoom-in aspect-square'
                  aria-label='Open image in full-screen viewer'
                >
                  {item.type === MediaType.VIDEO ? (
                    <>
                      <video
                        src={item.url}
                        poster={item.metadata?.thumbnailUrl}
                        className='object-cover w-full h-full'
                        muted
                        loop
                        autoPlay
                        playsInline
                      />
                      <div className='absolute inset-0 flex items-center justify-center transition-opacity opacity-0 bg-black/30 hover:opacity-100'>
                        <Icon icon='play' className='w-16 h-16 text-white' />
                      </div>
                    </>
                  ) : (
                    <img
                      src={item.url}
                      alt={item.altText || ''}
                      className='object-cover w-full h-full'
                      draggable={false}
                    />
                  )}
                </button>
              </Carousel.Item>
            ))}
          </Carousel.Content>
        </Carousel>

        {/* Pagination dots — overlaid bottom-centre, brand-accent active.
            Container is click-through so only the dots (not the empty strip
            beside them) intercept taps meant for the open-lightbox hero. */}
        <div className='absolute inset-x-0 bottom-3 z-10 flex items-center justify-center gap-1.5 pointer-events-none'>
          {media.map((item, i) => (
            <button
              key={item.id || i}
              type='button'
              aria-label={`Show image ${i + 1} of ${media.length}`}
              aria-current={i === currentIndex ? 'true' : undefined}
              onClick={(e) => {
                e.stopPropagation()
                onIndexChange(i)
              }}
              className={`pointer-events-auto h-2 rounded-full transition-all ${
                i === currentIndex ? 'w-5 bg-accent' : 'w-2 bg-white/60 hover:bg-white/90'
              }`}
            />
          ))}
        </div>

        {/* Tap-to-zoom hint chip — desktop only */}
        <div className='hidden md:flex absolute top-3 right-3 items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white rounded-full bg-black/55 backdrop-blur-sm pointer-events-none'>
          <Icon icon='search' className='w-3.5 h-3.5' />
          Click to zoom
        </div>
      </div>

      {/* Thumbnail strip */}
      {/* contain:inline-size zeroes the row's min-content contribution: mobile
          Chrome sizes the layout viewport from block min-content, so a thumb
          row wider than the screen otherwise ratchets the whole page wider. */}
      <div ref={thumbsRef} className='flex overflow-x-auto gap-2 pb-2 scroll-smooth [contain:inline-size]'>
        {media.map((item, index) => (
          <button
            key={item.id || index}
            type='button'
            data-index={index}
            onClick={() => onIndexChange(index)}
            aria-label={`Show image ${index + 1} of ${media.length}`}
            aria-current={index === currentIndex ? 'true' : undefined}
            className={`relative shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-md overflow-hidden border-2 transition-all
              ${index === currentIndex ? 'border-accent ring-2 ring-accent/30' : 'border-transparent hover:border-foreground/30'}`}
          >
            {item.type === MediaType.VIDEO ? (
              <div className='relative w-full h-full'>
                <img
                  src={item.metadata?.thumbnailUrl || item.url}
                  alt={item.altText || ''}
                  className='object-cover w-full h-full'
                />
                <div className='absolute inset-0 flex items-center justify-center bg-black/30'>
                  <Icon icon='play' className='w-6 h-6 text-white' />
                </div>
              </div>
            ) : (
              <img src={item.url} alt={item.altText || ''} className='object-cover w-full h-full' />
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
